<template>
  <!-- The rail (d42): the tablet width, or a collapsed desktop sidebar.
       64 wide: "Show sidebar" (collapsed desktop only), the mark (the
       account menu), a 44 px button per place, then you. -->
  <nav class="rail" aria-label="Places">
    <button v-if="canExpand" type="button" class="rail-btn" :title="`Show sidebar (${keyLabel})`" aria-label="Show sidebar" @click="expand">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5h16v14H4zM9 5v14" /></svg>
    </button>
    <button ref="logo" type="button" class="rail-btn rail-logo" title="Menu" aria-label="Menu" @click="openMenu">
      <ClMark :size="26" color="var(--cl-accent)" />
    </button>
    <router-link
      v-for="p in places"
      :key="p.id"
      :to="p.path"
      class="rail-btn rail-place"
      :class="{ 'is-active': selectedPlace === p.id }"
      :title="p.label"
      :aria-label="p.label"
    >
      <ClIcon :path="p.icon" :size="20" />
      <span v-if="p.id === 'inbox' && inboxCount > 0" class="rail-badge">{{ inboxCount }}</span>
    </router-link>
    <span class="rail-spacer" />
    <router-link v-if="person" :to="`/users/${encodeURIComponent(person.username)}`" class="rail-btn rail-me" title="You" aria-label="Your profile">
      <PersonDot :name="person.name" color="var(--cl-accent)" :picture="person.picture" :size="36" />
    </router-link>
  </nav>
</template>

<script>
import ClIcon from "@/components/kit/ClIcon.vue";
import ClMark from "@/components/kit/ClMark.vue";
import PersonDot from "@/components/kit/PersonDot.vue";
import { railPlaces } from "./places.js";
import { setCollapsed, shellState } from "./shellState.js";
import { sidebarKeyLabel } from "./shortcuts.js";
import { accountMenu } from "./accountMenu.js";
import { me } from "./me.js";
import sessionLists from "@/stores/sessionLists.js";

export default {
  name: "Rail",
  components: { ClIcon, ClMark, PersonDot },
  props: {
    selectedPlace: { type: String, default: null },
    // A collapsed desktop sidebar can open again from here.
    canExpand: { type: Boolean, default: false },
  },
  computed: {
    places() {
      return railPlaces({ ...sessionLists.counts.value, inbox: shellState.badges.inbox });
    },
    inboxCount() {
      return shellState.badges.inbox;
    },
    person() {
      return me.value;
    },
    keyLabel() {
      return sidebarKeyLabel();
    },
  },
  methods: {
    expand() {
      setCollapsed(false);
    },
    openMenu() {
      accountMenu.open(this.$refs.logo);
    },
  },
};
</script>

<style scoped>
.rail {
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100dvh;
  width: 64px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 0 10px;
  background: var(--cl-bg);
  border-right: 1px solid var(--border-subtle);
  box-sizing: border-box;
  overflow-y: auto;
}
.rail-btn {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--cl-label-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
}
.rail-btn:hover {
  background: var(--cl-hover);
  color: var(--cl-label-2);
}
.rail-place.is-active {
  background: var(--cl-accent-fill);
  color: var(--cl-accent-text-strong);
}
.rail-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  font: 700 10px/16px var(--cl-font);
  text-align: center;
  box-sizing: border-box;
}
.rail-spacer {
  flex: 1;
}
.rail-me :deep(.person-dot) {
  font-size: 12px !important;
}
</style>
